/* eslint-disable react/jsx-no-bind */
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Container,
  Typography,
  Box,
} from '@mui/material';
import * as d3 from 'd3';
import React, { useState, useEffect } from 'react';

import { IndiaGeoFeatures } from '../../data/mockGeoFeatures';
import logger from '../../utils/logger';

const GeoMap = () => {
  const [filter, setFilter] = useState('completed'); // Default filter
  const [indiaGeoJSON, setIndiaGeoJSON] = useState(null); // India map state

  // Booking data for each state
  const bookingData = {
    Telangana: { completed: 90, pending: 20, canceled: 5 },
    'Andhra Pradesh': { completed: 80, pending: 30, canceled: 10 },
    'Madhya Pradesh': { completed: 70, pending: 25, canceled: 15 },
    'Tamil Nadu': { completed: 95, pending: 10, canceled: 5 },
    Karnataka: { completed: 85, pending: 15, canceled: 8 },
  };

  // Color scale for filtering
  const colorScale = {
    completed: '#76c7c0', // Green
    pending: '#ffcc00', // Yellow
    canceled: '#f44336', // Red
  };

  // Load India GeoJSON (country boundary)
  useEffect(() => {
    d3.json('india-soi.geojson') // Ensure it's in the public folder
      .then((data) => {
        setIndiaGeoJSON(data);
      })
      .catch((error) => {
        logger.error('Error loading India map:', error);
      });
  }, []);

  // Function to draw the map
  const drawMap = (filter) => {
    d3.select('#map-container').selectAll('*').remove(); // Clear previous map

    const width = 700,
      height = 600;

    const svg = d3
      .select('#map-container')
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    const projection = d3
      .geoMercator()
      .center([80, 23]) // Center on India
      .scale(1200)
      .translate([width / 2, height / 2]);

    const path = d3.geoPath().projection(projection);

    // Draw India Map
    if (indiaGeoJSON) {
      svg
        .selectAll('path')
        .data(indiaGeoJSON.features)
        .enter()
        .append('path')
        .attr('d', path)
        .attr('fill', '#E0E0E0') // Light grey for country
        .attr('stroke', '#333')
        .attr('stroke-width', 0.5);
    }

    // Plot State Data Points (Telangana, AP, MP, TN, KA)
    IndiaGeoFeatures.features.forEach((stateFeature) => {
      const stateName = stateFeature.properties.state;
      const coordinates = stateFeature.geometry.coordinates;

      svg
        .selectAll(`circle.${stateName}`)
        .data(coordinates)
        .enter()
        .append('circle')
        .attr('class', stateName)
        .attr('cx', (d) => projection(d)[0])
        .attr('cy', (d) => projection(d)[1])
        .attr('r', 6) // Bigger for visibility
        .attr('fill', bookingData[stateName] ? colorScale[filter] : '#ccc')
        .attr('stroke', '#000')
        .attr('stroke-width', 1)
        .on('mouseover', (event, d) => {
          d3.select('#tooltip')
            .html(
              `${stateName}: ${bookingData[stateName]?.[filter]} ${filter} bookings`,
            )
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 20}px`)
            .style('display', 'block');
        })
        .on('mouseout', () => {
          d3.select('#tooltip').style('display', 'none');
        });
    });
  };

  // Redraw map when India data or filter changes
  useEffect(() => {
    if (indiaGeoJSON) {
      drawMap(filter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, indiaGeoJSON]);

  return (
    <Container>
      <Typography variant="h6" gutterBottom>
        India Booking Heatmap
      </Typography>
      <Box marginBottom={2}>
        <RadioGroup
          row
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <FormControlLabel
            value="completed"
            control={<Radio />}
            label="Completed Bookings"
          />
          <FormControlLabel
            value="pending"
            control={<Radio />}
            label="Pending Bookings"
          />
          <FormControlLabel
            value="canceled"
            control={<Radio />}
            label="Canceled Bookings"
          />
        </RadioGroup>
      </Box>
      <div id="map-container" style={{ marginBottom: '20px' }}></div>
      <div
        id="tooltip"
        style={{
          position: 'absolute',
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '5px',
          borderRadius: '5px',
          display: 'none',
          pointerEvents: 'none',
        }}
      ></div>
    </Container>
  );
};

export default GeoMap;
